import { sleep } from "@glideapps/ts-necessities";
import { allSettled, isRunningOnEmulators, isUndefinedish, logInfo } from "@glide/support";

import { loadPageJavascript } from "@glide/common-core/dist/js/support/browser-hacks";
import { getFeatureSettingStable } from "@glide/common-core/dist/js/feature-settings";
import { getCredentials } from "@glide/auth-controller-core";

export const googleDriveScope = "https://www.googleapis.com/auth/drive.file";

let initPromise: Promise<void> | undefined;
let initialized = false;

const waitTime = 500;
const maxInitWaitIterations = 40;
// If we don't get gapi in time then we assume we're never getting it.
// At one point we bumped this down to 10 seconds but some of our users have
// extremely poor network connections, and this still isn't enough time.
const maxWaitTime = 2 * maxInitWaitIterations * waitTime;

let gapiLoaded = false;

async function waitForGapi(): Promise<boolean> {
    let waits = 0;
    while (gapiLoaded === false && waits < maxInitWaitIterations) {
        await sleep(waitTime);
        waits++;
    }

    return gapiLoaded;
}

(window as any).gapiInitializedCallback = () => {
    gapiLoaded = true;
};

let loadedGSI = false;
const initGSI = (): Promise<void> => {
    return new Promise(r => {
        if (loadedGSI) return;
        loadPageJavascript("https://accounts.google.com/gsi/client", scriptElem => {
            scriptElem.defer = true;
            scriptElem.type = "text/javascript";
            scriptElem.onload = async () => {
                while (google === undefined) await sleep(150);
                return r();
            };
        });
        loadedGSI = true;
    });
};

async function doInit(): Promise<void> {
    if (isRunningOnEmulators()) {
        logInfo("skipping init google api because running on emulators");
        return;
    }

    logInfo("initing google api");
    if (await getFeatureSettingStable("useGoogleIdentityServices")) {
        loadPageJavascript("https://apis.google.com/js/api.js", undefined, () => (gapiLoaded = true));

        const [gapiValid] = await allSettled([waitForGapi(), initGSI()]);
        if (!gapiValid) return;
    } else {
        // As of 2020-02-17, this was the official entrypoint for Google APIs.
        // See https://github.com/google/google-api-javascript-client/issues/399#issuecomment-569415878
        // as to why we have to inject a callback name.
        loadPageJavascript("https://apis.google.com/js/platform.js?onload=gapiInitializedCallback");
        if (!(await waitForGapi())) return;
    }

    const loadedFlag = "loaded";
    const loadPromise = new Promise<typeof loadedFlag>(async (resolve, reject) => {
        let waits = 0;
        if (await getFeatureSettingStable("useGoogleIdentityServices")) {
            gapi.load("picker", {
                callback: async () => {
                    resolve("loaded");
                },
                onerror: (err: any) => reject(err),
            });
        } else {
            gapi.load("client:picker", {
                callback: async () => {
                    // gapi.client is not always ready when this fires.
                    // See https://github.com/google/google-api-javascript-client/issues/399
                    while (gapi.client === undefined && waits < maxInitWaitIterations) {
                        await sleep(waitTime);
                        waits++;
                    }
                    if (gapi.client === undefined) {
                        reject(new Error("Google API client was not installed"));
                        return;
                    }
                    // Even though .load is a Then'able, this sometimes interacts extremely poorly with
                    // Promise support. It's better to up-convert to a proper Promise whenever possible.
                    (gapi.client as any)
                        .load("https://www.googleapis.com/discovery/v1/apis/drive/v3/rest")
                        .then(() => resolve(loadedFlag))
                        .catch(reject);
                },

                onerror: (err: any) => reject(err),
            });
        }
    });
    // There are many, many things that could have gone wrong in the process of just setting up
    // the Google API. We have to establish a timeout at some point, and that timeout is maxWaitTime.
    // We can't use withTimeout here because we want to throw the actual error involved;
    // this will bubble up to consumers, who will ultimately pop a modal showing said error.
    const loadResult = await Promise.race([loadPromise, (() => sleep(maxWaitTime).then(() => "timeout"))()]);
    if (loadResult !== loadedFlag) {
        throw new Error("Timed out trying to load Google Drive support");
    }

    if (!(await getFeatureSettingStable("useGoogleIdentityServices"))) {
        const creds = getCredentials();
        // Even though .init is a Then'able, this sometimes interacts extremely poorly with
        // Promise support. It's better to up-convert to a proper Promise whenever possible.
        const clientPromise = new Promise((resolve, reject) =>
            gapi.client
                .init({
                    apiKey: creds.developerKey,
                    clientId: creds.clientId,
                    discoveryDocs: creds.discoveryDocs,
                    scope: `email`,
                })
                .then(() => resolve(loadedFlag))
                .catch(reject)
        );
        const clientResult = await Promise.race([clientPromise, sleep(maxWaitTime).then(() => "timeout")]);
        if (clientResult !== loadedFlag) {
            throw new Error(
                "Timed out trying to initialize Google APIs. This is usually caused by a browser plugin (e.g. ad blockers), blocking the connection to Google."
            );
        }
    }

    initialized = true;
}

export async function init(): Promise<void> {
    if (initPromise === undefined) {
        initPromise = doInit();
    }
    return initPromise;
}

export async function getGoogleAsync(): Promise<typeof google | undefined> {
    await init();
    if (!initialized) return undefined;
    return (window as any).google;
}

export const googleConnectionFailureMessage = "Could not connect to Google.";

async function getGAPIAsync(): Promise<typeof gapi | undefined> {
    await init();
    if (!initialized) return undefined;
    return (window as any).gapi;
}

// See https://stackoverflow.com/a/59175972
// If we don't omit the "then", then TypeScript incorrectly complains about
// self-referential "then" methods.
export async function getAuth2(): Promise<Omit<gapi.auth2.GoogleAuth, "then"> | undefined> {
    await init();
    if (!initialized) return undefined;
    const instance = gapi.auth2.getAuthInstance();
    // This can be `null`
    if (isUndefinedish(instance)) return undefined;
    return instance as any;
}

export async function setGAPIAccessToken(accessToken: string): Promise<void> {
    const gapi = await getGAPIAsync();
    if (gapi === undefined) return;
    logInfo("setting GAPI access token", accessToken);
    gapi.client.setToken({ access_token: accessToken });
}
